import type { Doc } from "@Aspecto/backend/convex/_generated/dataModel";

import { api } from "@Aspecto/backend/convex/_generated/api";
import { useMutation } from "convex/react";
import { createFileRoute } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DropdownSelect } from "@/components/ui/dropdown-select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionCard } from "@/components/ui/section-card";
import { Switch } from "@/components/ui/switch";
import { groupLabelOptions } from "@/lib/group-labels";
import { useCurrentUser, useCurrentCompany } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/admin/")({
  component: OrganizationSettings,
});

function OrganizationSettings() {
  const { data: company } = useCurrentCompany();
  const { data: currentUser } = useCurrentUser();
  const updateCompany = useMutation(api.companies.updateCompany);
  const updateGroupLabel = useMutation(api.companies.updateGroupLabel);
  const updateAllowedDomains = useMutation(api.companies.updateAllowedDomains);
  const updateAllowSelfRegistrationByDomain = useMutation(
    api.companies.updateAllowSelfRegistrationByDomain,
  );

  const [companyName, setCompanyName] = useState("");
  const [groupLabel, setGroupLabel] = useState<Doc<"companies">["groupLabel"]>(undefined);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [allowSelfRegistration, setAllowSelfRegistration] = useState(true);
  const [domainInput, setDomainInput] = useState("");
  const [pendingRemoveDomain, setPendingRemoveDomain] = useState<string | null>(null);
  const domainInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (company) {
      setCompanyName(company.name || "");
      setGroupLabel(company.groupLabel);
      const adminDomain = currentUser?.email?.split("@")[1];
      setAllowedDomains(company.allowedDomains ?? (adminDomain ? [adminDomain] : []));
      setAllowSelfRegistration(company.allowSelfRegistrationByDomain ?? true);
    }
  }, [company, currentUser]);

  const getOriginalDomains = () => {
    const adminDomain = currentUser?.email?.split("@")[1];
    return company?.allowedDomains ?? (adminDomain ? [adminDomain] : []);
  };

  const handleNameBlur = async () => {
    if (!companyName.trim()) {
      toast.error("Organization name is required");
      setCompanyName(company?.name || "");
      return;
    }
    if (companyName.trim() === (company?.name || "")) return;
    try {
      await updateCompany({ name: companyName.trim() });
    } catch {
      toast.error("Failed to save. Please try again.");
      setCompanyName(company?.name || "");
    }
  };

  const handleLabelChange = async (value: string) => {
    const next = value as Doc<"companies">["groupLabel"];
    setGroupLabel(next);
    try {
      await updateGroupLabel({ groupLabel: next ?? "space" });
    } catch {
      toast.error("Failed to save. Please try again.");
      setGroupLabel(company?.groupLabel);
    }
  };

  const saveDomains = async (domains: string[]) => {
    try {
      await updateAllowedDomains({ allowedDomains: domains });
    } catch {
      toast.error("Failed to save. Please try again.");
    }
  };

  const handleSelfRegistrationToggle = async (checked: boolean) => {
    setAllowSelfRegistration(checked);
    try {
      await updateAllowSelfRegistrationByDomain({ enabled: checked });
    } catch {
      toast.error("Failed to save. Please try again.");
      setAllowSelfRegistration(!checked);
    }
  };

  const addDomain = (value: string) => {
    const d = value.trim().toLowerCase().replace(/^@/, "");
    if (!d || !d.includes(".") || allowedDomains.includes(d)) return;
    const updated = [...allowedDomains, d];
    setAllowedDomains(updated);
    void saveDomains(updated);
  };

  const removeDomain = (domain: string) => {
    const originalDomains = getOriginalDomains();
    if (originalDomains.includes(domain)) {
      setPendingRemoveDomain(domain);
    } else {
      const updated = allowedDomains.filter((d) => d !== domain);
      setAllowedDomains(updated);
      void saveDomains(updated);
    }
  };

  const confirmRemoveDomain = async () => {
    if (!pendingRemoveDomain) return;
    const updated = allowedDomains.filter((d) => d !== pendingRemoveDomain);
    setAllowedDomains(updated);
    setPendingRemoveDomain(null);
    await saveDomains(updated);
  };

  const handleDomainKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addDomain(domainInput);
      setDomainInput("");
    } else if (e.key === "Backspace" && !domainInput) {
      const last = allowedDomains[allowedDomains.length - 1];
      if (last) removeDomain(last);
    }
  };

  const commitDomain = () => {
    if (domainInput.trim()) {
      addDomain(domainInput);
      setDomainInput("");
    }
  };

  return (
    <>
      <div className="space-y-6">
        <SectionCard title="Organization" description="Your organization information">
          <div className="space-y-2">
            <Label htmlFor="companyName">Organization name</Label>
            <Input
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              onBlur={() => void handleNameBlur()}
            />
            <p className="text-xs text-muted-foreground">
              This name appears across the platform for all team members.
            </p>
          </div>
          <div className="mt-6 space-y-2">
            <Label htmlFor="groupLabel">Space label</Label>
            <DropdownSelect
              id="groupLabel"
              value={groupLabel ?? "space"}
              options={groupLabelOptions}
              onValueChange={(value) => void handleLabelChange(value)}
              placeholder="Select a label"
            />
            <p className="text-xs text-muted-foreground">
              Choose how spaces are labeled across the platform.
            </p>
          </div>
        </SectionCard>
        <SectionCard
          title="Allowed domains"
          description="Only people with these email domains can be invited or register"
        >
          <div className="space-y-2">
            <Label>Domains</Label>
            <div
              className="flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-lg border border-input bg-background px-3 py-2 focus-within:ring-1 focus-within:ring-ring cursor-text"
              onClick={() => domainInputRef.current?.focus()}
            >
              {allowedDomains.map((domain) => (
                <span
                  key={domain}
                  className="flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-sm font-medium"
                >
                  {domain}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDomain(domain);
                    }}
                    className="ml-0.5 rounded hover:text-destructive focus:outline-none"
                    tabIndex={-1}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                ref={domainInputRef}
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value.toLowerCase())}
                onKeyDown={handleDomainKeyDown}
                onBlur={commitDomain}
                placeholder={allowedDomains.length === 0 ? "e.g. company.com" : ""}
                className="min-w-32 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Press Enter or comma to add a domain. Admins can only invite people whose email
              matches an approved domain.
            </p>
          </div>
          <div className="mt-6 flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="allow-self-registration">
                Allow self-registration using approved domains
              </Label>
              <p className="text-xs text-muted-foreground">
                Users with an approved email domain can create an account and automatically join the
                organization.
              </p>
            </div>
            <Switch
              id="allow-self-registration"
              checked={allowSelfRegistration}
              onCheckedChange={(checked) => void handleSelfRegistrationToggle(checked)}
            />
          </div>
        </SectionCard>
      </div>

      <AlertDialog
        open={!!pendingRemoveDomain}
        onOpenChange={(open) => !open && setPendingRemoveDomain(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove domain?</AlertDialogTitle>
            <AlertDialogDescription>
              Removing a domain will prevent new users with that domain from joining. Existing users
              will keep access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setPendingRemoveDomain(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void confirmRemoveDomain()}>
              Remove anyway
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
